import { useState } from 'react';
import { Product, PaginatedResponse } from '../types/entities';
import { ApiError } from '../types/api';
import { ProductFormData } from '../types/forms';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

interface ProductQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const useProductsApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchProducts = async (queryParams?: ProductQueryParams): Promise<PaginatedResponse<Product>> => {
    try {
      clearError();
      const queryString = queryParams ? new URLSearchParams(
        Object.entries(queryParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      
      const url = queryString ? `/products?${queryString}` : `/products`;
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(url);
      
      const data: PaginatedResponse<Product> = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchProduct = async (id: number): Promise<Product> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const createProduct = async (productData: ProductFormData): Promise<Product> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const updateProduct = async (id: number, productData: Partial<ProductFormData>): Promise<Product> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deleteProduct = async (id: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchProducts,
    fetchProduct,
    createProduct,
    updateProduct,
    deleteProduct,
  };
};